/* eslint-disable max-len */
/* eslint-disable no-param-reassign */

import { PluginJsonConfig } from '../plugin/PluginJsonConfig';
import AbstractLayerTreeElement from './AbstractLayerTreeElement';
import AnnotationLayerConfig from './AnnotationLayerConfig';
import { IDashBoardTile } from './DashBoardTile';
import LayerConfig from './LayerConfig';
import LayerGroupConfig from './LayerGroupConfig';
import { EventBus, EVENTS } from '@/services/EventBus';
import { create } from "xmlbuilder2";


export default class MapContext {
  version = 1.0;

  globalParameters: any = {
    hoverSelectionDelay:200
  };

  cesiumParameters: any = {
    timeline: false,
    shadows: false,
    fxaa: false,
    fog: false,
    angles: [],
  };

  coords!: number[];

  zoom = 0;

  projection!: string;

  presentation!: string;

  showPresentation = false;

  webgl = false;

  bbox!:any;

  public dashboard: IDashBoardTile[]= [];

  public bookmarks : any[] = [];

  public layers: AbstractLayerTreeElement[] = [];

  public baseLayers : LayerConfig[] = [];

  public groupLayers : LayerConfig[] = [];

  public plugins!: PluginJsonConfig[];

  constructor(input?: unknown) {
    console.log(input);
    if (input) {
      this.checkValues(input);
      Object.assign(this, input);
    } else {
      throw Error('Paramètres manquants pour la création du contexte');
    }

    for (let i = 0; i < this.layers.length; i += 1) {
      // eslint-disable-next-line keyword-spacing
      if ((<any>this.layers[i]).childs !== undefined) {
        this.layers[i] = new LayerGroupConfig(this.layers[i]);
      } else if(this.layers[i].type=='annotations') {
        this.layers[i] = new AnnotationLayerConfig(this.layers[i]);
      }else {
        this.layers[i] = new LayerConfig(this.layers[i]);
      }
    }
    if (!this.cesiumParameters.angles || this.cesiumParameters.angles.length === 0) {
      this.cesiumParameters.angles = [];
      this.cesiumParameters.angles[0] = 0.0;
      this.cesiumParameters.angles[1] = -89.0;
    }
    if (!this.globalParameters.hoverSelectionDelay ) {
      this.globalParameters.hoverSelectionDelay = 200;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  checkValues(input:any) :void{
    if (input.version === undefined) {
      throw Error('Format de carte inconnu, il devrait contenir un champ version');
    }
    if (input.projection === undefined) {
      throw Error('Projection non définie');
    }
  }

  getLayers(): AbstractLayerTreeElement[] {
    console.log(this.layers);
    return this.layers;
  }

  getLayerById(id:string): LayerConfig|undefined {
    return this.getOrderedLayerList().find((x) => x.id == id);
  }

  getLayerByUid(uid:string): LayerConfig|undefined {
    return this.getOrderedLayerList().find((x) => x.uid == uid);
  }

  flatten(arr: AbstractLayerTreeElement[]): LayerConfig[] {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return arr.reduce(function (flat: LayerConfig[], toFlatten) {
      const toConcat: LayerConfig[] = toFlatten instanceof LayerGroupConfig ? self.flatten(toFlatten.childs) : [<LayerConfig>toFlatten];
      return flat.concat(toConcat);
    }, []);
  }

  /**
   * Retourne la liste à plat et ordonnées des couches (noeuds enfants) et non la liste arborescente avec les groupes de couches
   */
  getOrderedLayerList(): LayerConfig[] {
    return this.flatten(this.layers);
  }

  getOrderedLayerGroupList(): AbstractLayerTreeElement[] {
    return this.layers
  }

  removeLayerFromList(layer: AbstractLayerTreeElement): void {
    // remove from current location
    if (layer.parent) {
      const indexStart = layer.parent.childs.indexOf(layer);
      layer.parent.childs.splice(indexStart, 1);
    } else {
      const indexStart = this.layers.indexOf(layer);
      this.layers.splice(indexStart, 1);
    }
  }

  getBackgroundGroup(): LayerGroupConfig {
    // eslint-disable-next-line keyword-spacing
    return (<LayerGroupConfig>this.layers.filter((cat) => cat instanceof LayerGroupConfig && cat.idGroup < 0)[0]);
  }

  changeLayerOrder(source: AbstractLayerTreeElement, target: AbstractLayerTreeElement, location: string): void {
    // remove from current location
    this.removeLayerFromList(source);

    // insert at target location
    // Drop before
    if (location === 'before') {
      if (target.parent) {
        const indexDest = target.parent.childs.indexOf(target);
        target.parent.childs.splice(indexDest, 0, source);
        // update parent
        source.parent = target.parent;
      } else {
        const indexDest = this.layers.indexOf(target);
        this.layers.splice(indexDest, 0, source);
        // update parent
        source.parent = undefined;
      }
    } else if (location === 'after') { // Drop after
      if (target.parent) {
        const indexDest = target.parent.childs.indexOf(target);
        target.parent.childs.splice(indexDest + 1, 0, source);
        // update parent
        source.parent = target.parent;
      } else {
        const indexDest = this.layers.indexOf(target);
        this.layers.splice(indexDest + 1, 0, source);
        // update parent
        source.parent = undefined;
      }
    } else if (location === 'into' && target instanceof LayerGroupConfig) { // Drop into
      target.childs.unshift(source);
      // update parent
      source.parent = target;
    }
    EventBus.$emit(EVENTS.LAYER_ORDER_CHANGE);
  }


  toJson(): any {
    const retour = {
      version: this.version,
      globalParameters: this.globalParameters,
      presentation: this.presentation,
      showPresentation: this.showPresentation,
      cesiumParameters: this.cesiumParameters,
      coords: this.coords,
      zoom: this.zoom,
      projection: this.projection,
      webgl: this.webgl,
      layers: <any>[],
      plugins: <any>[],
      dashboard: this.dashboard,
      bookmarks: this.bookmarks,
    };
    this.layers.forEach((layer) => retour.layers.push(layer.toJson()));
    return retour;
  }

  toMapLibre() {
    const retour = {
        version: 8,
        name: "",
        id: "",
        glyphs: "https://data.geopf.fr/annexes/ressources/vectorTiles/fonts/{fontstack}/{range}.pbf",
        sprite: "https://data.geopf.fr/annexes/ressources/vectorTiles/styles/PLAN.IGN/sprite/PlanIgn",
        center: this.coords,
        zoom: this.zoom,
        sources:  <any>{},
        layers: <any>[]
    };

    const reverseLayers = [...this.getOrderedLayerList()].reverse();

    for(const layer of reverseLayers) {
        const aLayers = retour.layers;
        const layerMapLibre = layer.toMapLibre();
        if (! layerMapLibre) {
            continue;
        }

        const {layers: mapLibreLayers, source: mapLibreSources} = layerMapLibre;
        if (Array.isArray(mapLibreLayers)) {
            aLayers.push(...mapLibreLayers);
        } else {
            aLayers.push(mapLibreLayers);
        }

        Object.assign(retour.sources, mapLibreSources)
    }

    return retour;
  }

  toQgis(mapExtent: any) {
    const root = create().dtd({
      name: 'qgis',
      pubID: 'http://mrcc.com/qgis.dtd',
      sysID: 'SYSTEM'
    }).ele('qgis')

    const elprojectCrs = root.ele('projectCrs');
    const elspatialrefsys = elprojectCrs.ele('spatialrefsys');
    elspatialrefsys.ele('srsid').txt('3857');
    elspatialrefsys.ele('srid').txt('3857');
    elspatialrefsys.ele('authid').txt('EPSG:3857');

    const elTagLayerTreeGroup = root.ele('layer-tree-group')
    const elProjectlayers = root.ele('projectlayers')

    this.layers.forEach((layer) => {
      layer.toQgis(elTagLayerTreeGroup, elProjectlayers)
    });

    const elmapcanvas = root.ele('mapcanvas');
    elmapcanvas.ele('units').txt('degrees');

    const elextent = elmapcanvas.ele('extent');
    elextent.ele('xmin').txt(mapExtent[0]);
    elextent.ele('ymin').txt(mapExtent[1]);
    elextent.ele('xmax').txt(mapExtent[2]);
    elextent.ele('ymax').txt(mapExtent[3]);

    const elproperties = root.ele('properties');
    const elSpatialRefSys = elproperties.ele('SpatialRefSys');
    elSpatialRefSys.ele('ProjectionsEnabled', {type : 'int'}).txt('1');

    return root.end({ prettyPrint: true })
  }
}
